import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const WebsiteTrafficTrendChart = ({ data, interval, graphName }) => {
  // Function to format traffic data for chart based on interval type
  const formatTrafficDataForChart = (data, intervalLabel) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data.map((item) => {
      // item format: [visit_date, total_visits, unique_visitors, sessions, new_visitors, avg_page_load_ms]
      const dateStr = item[0];
      let displayName;

      // Format date based on the date string format
      if (typeof dateStr === "string") {
        if (dateStr.includes(" ")) {
          // Timestamp format (for minutes/hours)
          const date = new Date(dateStr);
          if (intervalLabel.includes("Minute")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            });
          } else {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              hour12: true,
            });
          }
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Date format YYYY-MM-DD
          const date = new Date(dateStr);
          displayName = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } else {
          displayName = dateStr;
        }
      } else {
        // Fallback to string conversion
        displayName = String(dateStr);
      }

      return {
        name: displayName,
        totalVisits: item[1] || 0,
        uniqueVisitors: item[2] || 0,
        sessions: item[3] || 0,
        newVisitors: item[4] || 0,
        avgPageLoadMs: item[5] ? parseFloat(item[5]) : 0,
        label: displayName,
      };
    });
  };

  const chartData = formatTrafficDataForChart(data, interval);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-placeholder">
        📊 No data available
        <small>Try selecting a different date range</small>
      </div>
    );
  }

  // Custom tooltip to show all metrics
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            fontSize: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: "0 0 8px 0", fontWeight: "700", color: "#2d3748", borderBottom: "1px solid #e2e8f0", paddingBottom: "6px" }}>
            {label}
          </p>
          
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", margin: "4px 0", color: "#667eea" }}>
            <span>Total Visits:</span>
            <span style={{ fontWeight: "700" }}>{dataPoint.totalVisits.toLocaleString()}</span>
          </div>
          
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", margin: "4px 0", color: "#3B82F6" }}>
            <span>Unique Visitors:</span>
            <span style={{ fontWeight: "700" }}>{dataPoint.uniqueVisitors.toLocaleString()}</span>
          </div>
          
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", margin: "4px 0", color: "#10B981" }}>
            <span>Sessions:</span>
            <span style={{ fontWeight: "700" }}>{dataPoint.sessions.toLocaleString()}</span>
          </div>
          
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", margin: "4px 0", color: "#EC4899" }}>
            <span>New Visitors:</span>
            <span style={{ fontWeight: "700" }}>{dataPoint.newVisitors.toLocaleString()}</span>
          </div>
          
          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", margin: "4px 0", color: "#F59E0B", borderTop: "1px solid #e2e8f0", paddingTop: "6px", marginTop: "6px" }}>
            <span>Avg Load Time:</span>
            <span style={{ fontWeight: "700" }}>{dataPoint.avgPageLoadMs.toFixed(2)}ms</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          angle={chartData.length > 10 ? -45 : 0}
          textAnchor={chartData.length > 10 ? "end" : "middle"}
          height={chartData.length > 10 ? 60 : 30}
        />
        <YAxis
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
          label={{
            value: "Total Visits",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: "12px", fill: "#718096" },
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px" }}
          iconType="line"
        />
        
        {/* Total Visits - Single Line */}
        <Line
          type="monotone"
          dataKey="totalVisits"
          stroke="#667eea"
          strokeWidth={3}
          dot={{ fill: "#667eea", r: 5 }}
          activeDot={{ r: 8 }}
          name="Total Visits"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default WebsiteTrafficTrendChart;